import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { authAPI, orderAPI, productAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  FiX, FiArrowLeft, FiRefreshCw, FiCheck, FiPlus, FiMinus,
  FiMapPin, FiTag, FiChevronRight, FiZap, FiShoppingBag, FiChevronUp,
} from 'react-icons/fi'
import './CheckoutDrawer.css';

const INDIAN_STATES = [
  'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat',
  'Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh',
  'Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab',
  'Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh',
  'Uttarakhand','West Bengal','Delhi','Jammu and Kashmir','Ladakh',
];

const PREPAID_COUPON = 'UPI10';

const PAYMENT_OPTIONS = [
  { value: 'upi',        label: 'UPI',                 icon: '📱', desc: 'GPay, PhonePe, Paytm & more', badge: '10% OFF', razorpayMethod: 'upi',        isCod: false },
  { value: 'card',       label: 'Credit / Debit Card', icon: '💳', desc: 'Visa, Mastercard, RuPay',      badge: '10% OFF', razorpayMethod: 'card',       isCod: false },
  { value: 'netbanking', label: 'Net Banking',          icon: '🏦', desc: 'All major Indian banks',       badge: '10% OFF', razorpayMethod: 'netbanking', isCod: false },
  { value: 'cod',        label: 'Cash on Delivery',     icon: '💵', desc: 'Pay when order arrives',       badge: null,      razorpayMethod: null,         isCod: true  },
];

/* ── OTP boxes ───────────────────────────────────────────── */
const OtpBoxes = ({ otp, setOtp, refs }) => {
  const handleChange = (i, val) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...otp]; next[i] = val.slice(-1); setOtp(next);
    if (val && i < 5) refs.current[i + 1]?.focus();
  };
  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) refs.current[i - 1]?.focus();
  };
  const handlePaste = (e) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (text.length === 6) { setOtp(text.split('')); refs.current[5]?.focus(); }
  };
  return (
    <div className="cd-otp-boxes" onPaste={handlePaste}>
      {otp.map((digit, i) => (
        <input key={i} ref={el => refs.current[i] = el} type="text" inputMode="numeric" maxLength={1}
          className={`cd-otp-box ${digit ? 'cd-otp-box--filled' : ''}`} value={digit}
          onChange={e => handleChange(i, e.target.value)} onKeyDown={e => handleKeyDown(i, e)} />
      ))}
    </div>
  );
};

/* ── Step dots ───────────────────────────────────────────── */
const StepDots = ({ steps, current }) => (
  <div className="cd-steps">
    {steps.map((label, i) => (
      <React.Fragment key={label}>
        <div className={`cd-step ${i < current ? 'cd-step--done' : ''} ${i === current ? 'cd-step--active' : ''}`}>
          <div className="cd-step__dot">{i < current ? <FiCheck size={10} /> : i + 1}</div>
          <span className="cd-step__label">{label}</span>
        </div>
        {i < steps.length - 1 && <div className={`cd-step__line ${i < current ? 'cd-step__line--done' : ''}`} />}
      </React.Fragment>
    ))}
  </div>
);

/* ── Order Summary Popup ─────────────────────────────────── */
const OrderSummaryPopup = ({ displayItems, subtotal, shipping, prepaidDiscount, promoDiscount, promoCode, total, onClose, updateItem, removeItem, step }) => (
  <div className="cd-summary-popup-overlay" onClick={onClose}>
    <div className="cd-summary-popup" onClick={e => e.stopPropagation()}>
      <div className="cd-summary-popup__header">
        <span><FiShoppingBag size={15} /> Order Summary</span>
        <button onClick={onClose}><FiX size={17} /></button>
      </div>
      <div className="cd-summary-popup__items">
        {displayItems.map(item => (
          <div key={item.id} className="cd-summary-popup__item">
            {item.product_image && (
              <img src={item.product_image} alt={item.product_name} className="cd-summary-popup__img" />
            )}
            <div className="cd-summary-popup__info">
              <p className="cd-summary-popup__name">{item.product_name}</p>
              {item.variant?.pack_label && (
                <p className="cd-summary-popup__variant">{item.variant.pack_label}</p>
              )}
              {item.id !== 'pending' ? (
                <div className="cd-popup-qty">
                  <button className="cd-popup-qty__btn"
                    onClick={() => item.quantity > 1 ? updateItem(item.id, item.quantity - 1) : removeItem(item.id)}>
                    <FiMinus size={10} />
                  </button>
                  <span className="cd-popup-qty__val">{item.quantity}</span>
                  <button className="cd-popup-qty__btn"
                    onClick={() => updateItem(item.id, item.quantity + 1)}>
                    <FiPlus size={10} />
                  </button>
                </div>
              ) : (
                <p className="cd-summary-popup__qty">Qty: {item.quantity}</p>
              )}
            </div>
            <p className="cd-summary-popup__price">₹{Number(item.subtotal).toFixed(0)}</p>
          </div>
        ))}
      </div>
      <div className="cd-summary-popup__footer">
        <div className="cd-summary-popup__row">
          <span>Subtotal</span><span>₹{subtotal.toFixed(0)}</span>
        </div>
        <div className="cd-summary-popup__row">
          <span>Shipping</span>
          <span style={{ color: shipping === 0 ? '#2d6a4f' : 'inherit', fontWeight: shipping === 0 ? 700 : 400 }}>
            {shipping === 0 ? 'FREE' : `₹${shipping}`}
          </span>
        </div>
        {step === 'payment' && prepaidDiscount > 0 && (
          <div className="cd-summary-popup__row cd-summary-popup__row--discount">
            <span>Prepaid Discount (UPI10)</span>
            <span>–₹{prepaidDiscount.toFixed(0)}</span>
          </div>
        )}
        {step === 'payment' && promoDiscount > 0 && (
          <div className="cd-summary-popup__row cd-summary-popup__row--discount">
            <span>Promo ({promoCode})</span>
            <span>–₹{promoDiscount.toFixed(0)}</span>
          </div>
        )}
        <div className="cd-summary-popup__row cd-summary-popup__row--total">
          <span>Total</span>
          <span>₹{step === 'payment' ? total.toFixed(0) : (subtotal + shipping).toFixed(0)}</span>
        </div>
      </div>
    </div>
  </div>
);

/* ── Main Component ──────────────────────────────────────── */
const CheckoutDrawer = () => {
  const { user, requestOtp, loginWithOtp, registerWithOtp, updateUser } = useAuth();
  const { cart, totalPrice, clearCart, checkoutDrawerOpen, setCheckoutDrawerOpen, fetchCart, pendingItem, clearPendingItem, updateItem, removeItem } = useCart();
  const navigate = useNavigate();

  const [step, setStep] = useState(user ? 'address' : 'phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const otpRefs = useRef([]);

  const [addresses, setAddresses] = useState([]);
  const [selectedAddr, setSelectedAddr] = useState(null);
  const [showNewAddrForm, setShowNewAddrForm] = useState(false);
  const [addrLoading, setAddrLoading] = useState(false);
  const [newAddr, setNewAddr] = useState({ name: '', phone: '', address_line1: '', address_line2: '', city: '', state: '', pincode: '' });

  const [paymentMethod, setPaymentMethod] = useState('upi');

  /* ── Prepaid coupon (auto) ── */
  const [couponCode, setCouponCode]   = useState('');
  const [couponData, setCouponData]   = useState(null);

  /* ── Manual promo code ── */
  const [promoCode, setPromoCode]         = useState('');
  const [promoData, setPromoData]         = useState(null);
  const [promoLoading, setPromoLoading]   = useState(false);

  const [placing, setPlacing]   = useState(false);
  const [loading, setLoading]   = useState(false);
  const [note, setNote]         = useState('');
  const [summaryOpen, setSummaryOpen] = useState(false);

  /* ── Derived values ── */
  const cartItems    = cart?.items || [];
  const displayItems = cartItems.length > 0
    ? cartItems
    : pendingItem
      ? [{ id: 'pending', product_name: pendingItem.productName || 'Selected Product', variant: { pack_label: pendingItem.packLabel || '' }, quantity: pendingItem.quantity, subtotal: pendingItem.price * pendingItem.quantity }]
      : [];

  const subtotal = cartItems.length > 0 ? Number(totalPrice) : (pendingItem ? pendingItem.price * pendingItem.quantity : 0);
  const shipping  = subtotal >= 500 ? 0 : (subtotal > 0 ? 90 : 0);

  /* ── Stacked discounts ── */
  const prepaidDiscount = couponData && couponCode === PREPAID_COUPON
    ? Math.round(subtotal * 0.10)
    : 0;
  const promoDiscount = promoData ? Number(promoData.discount_amount) : 0;
  const totalDiscount = prepaidDiscount + promoDiscount;
const total = Number(
  Math.max(
    0,
    Number(subtotal || 0) -
    Number(totalDiscount || 0) +
    Number(shipping || 0)
  )
);

  /* ── Steps ── */
  const needsProfile = user && (!user.first_name || !user.email);
  const ALL_STEPS = !user
    ? ['Phone', 'OTP', 'Details', 'Address', 'Payment']
    : needsProfile
      ? ['Details', 'Address', 'Payment']
      : ['Address', 'Payment'];
  const STEP_INDEX = !user
    ? { phone: 0, otp: 1, profile: 2, address: 3, payment: 4 }
    : needsProfile
      ? { profile: 0, address: 1, payment: 2 }
      : { address: 0, payment: 1 };

  /* ── Reset on drawer open ── */
  useEffect(() => {
    if (checkoutDrawerOpen) {
      if (user) {
        if (!user.first_name || !user.email) {
          setStep('profile'); setFullName(user.first_name || ''); setEmail(user.email || '');
        } else {
          setStep('address'); fetchAddresses();
        }
      } else {
        setStep('phone'); setPhone(''); setOtp(['','','','','','']); setFullName(''); setEmail('');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutDrawerOpen, user]);

  /* ── Auto prepaid coupon on payment step ── */
  useEffect(() => {
    if (step !== 'payment' || subtotal <= 0) return;
    const selectedIsCod = PAYMENT_OPTIONS.find(p => p.value === paymentMethod)?.isCod;
    if (!selectedIsCod) {
      if (!couponData) {
        (async () => {
          try {
            const res = await productAPI.validateCoupon({ code: PREPAID_COUPON, order_amount: subtotal });
            const data = res.data;
            if (data && data.discount_amount) data.discount_amount = Number(data.discount_amount);
            setCouponData(data); setCouponCode(PREPAID_COUPON);
            toast.success(`🎉 10% prepaid discount applied! You save ₹${Math.round(subtotal * 0.10)}`);
          } catch {
            setCouponData({ discount_amount: Math.round(subtotal * 0.10), code: PREPAID_COUPON });
            setCouponCode(PREPAID_COUPON);
            toast.success(`🎉 Prepaid discount applied! You save ₹${Math.round(subtotal * 0.10)}`);
          }
        })();
      }
    } else {
      if (couponCode === PREPAID_COUPON) { setCouponCode(''); setCouponData(null); }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethod, step]);

  useEffect(() => {
    if (resendTimer > 0) { const t = setTimeout(() => setResendTimer(r => r - 1), 1000); return () => clearTimeout(t); }
  }, [resendTimer]);

  useEffect(() => {
    document.body.style.overflow = checkoutDrawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [checkoutDrawerOpen]);

  const fetchAddresses = useCallback(async () => {
    setAddrLoading(true);
    try {
      const res  = await authAPI.getAddresses();
      const data = res.data;
      const list = Array.isArray(data) ? data : (Array.isArray(data.results) ? data.results : []);
      setAddresses(list);
      if (list.length > 0) { setSelectedAddr(list.find(a => a.is_default) || list[0]); setShowNewAddrForm(false); }
      else { setShowNewAddrForm(true); setSelectedAddr(null); }
    } catch { setAddresses([]); setShowNewAddrForm(true); }
    finally { setAddrLoading(false); }
  }, []);

  const close = () => setCheckoutDrawerOpen(false);

  const handleBack = () => {
    if (step === 'otp') setStep('phone');
    else if (step === 'profile' && !user) setStep('otp');
    else if (step === 'address') {
      if (!user) setStep('profile');
      else if (needsProfile) setStep('profile');
      else close();
    }
    else if (step === 'payment') setStep('address');
  };

  const canGoBack =
    step === 'otp' || (step === 'profile' && !user) ||
    (step === 'address' && !user) || (step === 'address' && needsProfile) ||
    step === 'payment';

  /* ── Apply manual promo ── */
  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      const res  = await productAPI.validateCoupon({ code: promoCode.trim(), order_amount: subtotal });
      const data = res.data;
      if (data && data.discount_amount) data.discount_amount = Number(data.discount_amount);
      setPromoData(data);
      toast.success(`Promo applied! Saving ₹${Number(data.discount_amount).toFixed(0)}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Invalid promo code');
      setPromoData(null);
    } finally { setPromoLoading(false); }
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (phone.length !== 10) { toast.error('Enter a valid 10-digit number'); return; }
    setLoading(true);
    try {
      await requestOtp({ phone: '+91' + phone });
      setStep('otp'); setResendTimer(30);
      toast.success('OTP sent'); setTimeout(() => otpRefs.current[0]?.focus(), 200);
    } catch (err) {
      const errors = err.response?.data;
      if (errors && typeof errors === 'object') Object.values(errors).flat().forEach(m => toast.error(m));
      else toast.error('Failed to send OTP');
    } finally { setLoading(false); }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const code = otp.join('');
    if (code.length !== 6) { toast.error('Enter 6-digit OTP'); return; }
    setLoading(true);
    try {
      const loggedUser = await loginWithOtp({ phone: '+91' + phone, otp: code });
      toast.success(`Welcome back, ${loggedUser.first_name}! 👋`);
      setFullName(`${loggedUser.first_name || ''} ${loggedUser.last_name || ''}`.trim());
      setEmail(loggedUser.email || '');
      await fetchCart(); await fetchAddresses();
      setStep('address');
    } catch (err) {
      const isNewUser = err.response?.status === 404 || err.response?.data?.is_new_user;
      if (isNewUser) setStep('profile');
      else { toast.error(err.response?.data?.error || 'Invalid OTP'); setOtp(['', '', '', '', '', '']); otpRefs.current[0]?.focus(); }
    } finally { setLoading(false); }
  };

  const handleProfileContinue = async (e) => {
    e.preventDefault();
    if (!fullName.trim()) { toast.error('Enter your full name'); return; }
    const parts = fullName.trim().split(' ');
    const first_name = parts[0]; const last_name = parts.slice(1).join(' ');
    setLoading(true);
    try {
      if (user) {
        const res = await authAPI.updateProfile({ first_name, last_name, email: email.trim() });
        updateUser(res.data);
        setNewAddr(a => ({ ...a, name: fullName.trim(), phone: user.phone?.replace('+91', '') || '' }));
        await fetchAddresses(); setStep('address'); toast.success('Profile updated! 🌿');
      } else {
        const newUser = await registerWithOtp({ first_name, last_name, email: email.trim(), phone: '+91' + phone, otp: otp.join('') });
        toast.success(`Welcome, ${newUser.first_name}! 🌿`);
        await fetchCart();
        setNewAddr(a => ({ ...a, name: fullName.trim(), phone }));
        setShowNewAddrForm(true); await fetchAddresses(); setStep('address');
      }
    } catch (err) {
      const errors = err.response?.data;
      if (errors && typeof errors === 'object') Object.values(errors).flat().forEach(m => toast.error(m));
      else toast.error('Registration failed. Try again.');
      if (err.response?.data?.otp) { setOtp(['', '', '', '', '', '']); setStep('otp'); }
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    setLoading(true);
    try {
      await requestOtp({ phone: '+91' + phone });
      setResendTimer(30); setOtp(['', '', '', '', '', '']);
      toast.success('OTP resent'); setTimeout(() => otpRefs.current[0]?.focus(), 200);
    } catch { toast.error('Failed to resend OTP'); }
    finally { setLoading(false); }
  };

  const handleSaveNewAddr = async (e) => {
    e.preventDefault();
    if (!newAddr.name || !newAddr.phone || !newAddr.address_line1 || !newAddr.city || !newAddr.state || !newAddr.pincode) {
      toast.error('Please fill all required fields'); return;
    }
    setLoading(true);
    try {
      const res = await authAPI.addAddress({ ...newAddr, is_default: addresses.length === 0 });
      const saved = res.data;
      setAddresses(prev => [...prev, saved]); setSelectedAddr(saved); setShowNewAddrForm(false);
      toast.success('Address saved!'); setStep('payment');
    } catch { toast.error('Could not save address. Please try again.'); }
    finally { setLoading(false); }
  };

  const handlePlaceOrder = async () => {
    if (!selectedAddr) { toast.error('Select a delivery address'); return; }
    if (subtotal <= 0) { toast.error('Cart is empty'); return; }
    setPlacing(true);
    console.log("TOTAL =", total);
console.log("TYPE =", typeof total);

    const selectedOption = PAYMENT_OPTIONS.find(p => p.value === paymentMethod);
    const isCod = selectedOption?.isCod === true;

    try {
      const payload = {
        payment_method:  isCod ? 'cod' : 'razorpay',
        coupon_code:     promoData ? promoCode : (couponData ? couponCode : ''),
        customer_note:   note,
        address_id:      selectedAddr.id,
      };

      if (isCod) {
        const res = await orderAPI.place(payload);
        await clearCart(); clearPendingItem(); close();
        toast.success('Order placed! 🎉');
        navigate(`/orders/${res.data.order_id}`);
        setPlacing(false); return;
      }

      const rzpRes = await orderAPI.createRazorpayOrder({
  amount: Number(total)
});
      const { razorpay_order_id, amount: rzpAmount, key } = rzpRes.data;

      const options = {
        key, amount: rzpAmount, currency: 'INR', order_id: razorpay_order_id,
        name: 'Vedonica International', description: 'Ayurvedic Wellness Products',
        image: '/logo192.png', method: selectedOption?.razorpayMethod || undefined,
        handler: async (response) => {
          try {
            const verify = await orderAPI.verifyRazorpayPayment({
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
            });
            if (verify.data.success) {
              payload.razorpay_payment_id = response.razorpay_payment_id;
              payload.razorpay_order_id   = response.razorpay_order_id;
              const res = await orderAPI.place(payload);
              await clearCart(); clearPendingItem(); close();
              toast.success('Payment successful! 🎉');
              navigate(`/orders/${res.data.order_id}`);
            } else { toast.error('Payment verification failed'); }
          } catch { toast.error('Something went wrong after payment'); }
          finally { setPlacing(false); }
        },
        prefill: {
          name:    user?.full_name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim(),
          email:   user?.email || '',
          contact: user?.phone || '',
        },
        theme: { color: '#2d6a4f' },
        modal: { ondismiss: () => { toast.error('Payment cancelled'); setPlacing(false); } },
      };
      new window.Razorpay(options).open();
    } catch (err) {
      const errors = err.response?.data;
      if (typeof errors === 'object') Object.values(errors).flat().forEach(e => toast.error(e));
      else toast.error('Failed to place order. Try again.');
      setPlacing(false);
    }
  };

  if (!checkoutDrawerOpen) return null;

  return (
    <>
      <div className="cd-backdrop" onClick={close} />

      {summaryOpen && (
        <OrderSummaryPopup
          displayItems={displayItems}
          subtotal={subtotal}
          shipping={shipping}
          prepaidDiscount={prepaidDiscount}
          promoDiscount={promoDiscount}
          promoCode={promoCode}
          total={total}
          onClose={() => setSummaryOpen(false)}
          updateItem={updateItem}
          removeItem={removeItem}
          step={step}
        />
      )}

      <div className="cd-drawer">

        {/* ── Header ── */}
        <div className="cd-header">
          <div className="cd-header__left">
            {canGoBack && (
              <button className="cd-back-btn" onClick={handleBack} aria-label="Go back">
                <FiArrowLeft size={18} />
              </button>
            )}
            <span className="cd-header__brand"></span>
          </div>
          <button className="cd-close-btn" onClick={close} aria-label="Close"><FiX size={20} /></button>
        </div>

        <div className="cd-shipping-banner">🚚 Free shipping on orders above ₹500!</div>
        <div className="cd-steps-wrap">
          <StepDots steps={ALL_STEPS} current={STEP_INDEX[step] ?? 0} />
        </div>

        {/* ── Body ── */}
        <div className="cd-body">

          {/* Phone step */}
          {step === 'phone' && (
            <div className="cd-section cd-auth">
              <p className="cd-section__title">Enter your mobile number</p>
              <form onSubmit={handleSendOtp}>
                <div className="cd-phone-wrap">
                  <div className="cd-phone-prefix"><span>🇮🇳</span><span>+91</span></div>
                  <input type="tel" className="cd-phone-input" placeholder="10-digit mobile number"
                    value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    maxLength={10} autoFocus required />
                </div>
                <button type="submit" className="cd-btn cd-btn--primary" disabled={loading || phone.length !== 10}>
                  {loading ? <span className="cd-spinner-wrap"><span className="cd-spinner" /> Sending...</span> : <>Next <FiChevronRight size={16} /></>}
                </button>
              </form>
            </div>
          )}

          {/* OTP step */}
          {step === 'otp' && (
            <div className="cd-section cd-auth">
              <p className="cd-section__title">OTP sent to <strong>+91 {phone}</strong></p>
              <form onSubmit={handleVerifyOtp}>
                <OtpBoxes otp={otp} setOtp={setOtp} refs={otpRefs} />
                <button type="submit" className="cd-btn cd-btn--primary" disabled={loading || otp.join('').length !== 6}>
                  {loading ? <span className="cd-spinner-wrap"><span className="cd-spinner" /> Verifying...</span> : <>Verify & Continue <FiChevronRight size={16} /></>}
                </button>
              </form>
              <div className="cd-resend">
                {resendTimer > 0
                  ? <p>Resend in <strong>{resendTimer}s</strong></p>
                  : <button className="cd-resend-btn" onClick={handleResend} disabled={loading}><FiRefreshCw size={13} /> Resend OTP</button>}
              </div>
            </div>
          )}

          {/* Profile step */}
          {step === 'profile' && (
            <div className="cd-section cd-auth">
              <p className="cd-section__title">Create your account</p>
              <form onSubmit={handleProfileContinue}>
                <div className="cd-field" style={{ marginBottom: 12 }}>
                  <label className="cd-label">Full Name *</label>
                  <input className="cd-input" placeholder="Your full name"
                    value={fullName} onChange={e => setFullName(e.target.value)} required autoFocus />
                </div>
                <div className="cd-field" style={{ marginBottom: 14 }}>
                  <label className="cd-label">Email *</label>
                  <input className="cd-input" type="email" placeholder="yourname@example.com"
                    value={email} onChange={e => setEmail(e.target.value)} required />
                </div>
                <button type="submit" className="cd-btn cd-btn--primary" disabled={loading || !fullName.trim()}>
                  {loading ? <span className="cd-spinner-wrap"><span className="cd-spinner" /> Saving...</span> : <>Continue <FiChevronRight size={16} /></>}
                </button>
              </form>
            </div>
          )}

          {/* Address step */}
          {step === 'address' && (
            <div className="cd-section">
              <p className="cd-section__title"><FiMapPin size={15} /> Delivery Address</p>
              {addrLoading ? (
                <div className="cd-addr-loading"><span className="cd-spinner cd-spinner--green" /> Loading addresses...</div>
              ) : (
                <>
                  {addresses.length > 0 && !showNewAddrForm && (
                    <div className="cd-addr-list">
                      {addresses.map(a => (
                        <div key={a.id} className={`cd-addr-card ${selectedAddr?.id === a.id ? 'cd-addr-card--selected' : ''}`} onClick={() => setSelectedAddr(a)}>
                          <div className="cd-addr-radio">{selectedAddr?.id === a.id && <FiCheck size={11} />}</div>
                          <div className="cd-addr-info">
                            <p className="cd-addr-name">{a.name} · <span>{a.phone}</span></p>
                            <p className="cd-addr-line">{a.address_line1}{a.address_line2 ? ', ' + a.address_line2 : ''}</p>
                            <p className="cd-addr-line">{a.city}, {a.state} – {a.pincode}</p>
                            {a.is_default && <span className="cd-badge">Default</span>}
                          </div>
                        </div>
                      ))}
                      <button className="cd-add-addr-btn" onClick={() => { setShowNewAddrForm(true); setSelectedAddr(null); }}>
                        <FiPlus size={16} /> Add New Address
                      </button>
                      <button className="cd-btn cd-btn--primary" disabled={!selectedAddr} onClick={() => setStep('payment')}>
                        Deliver Here <FiChevronRight size={16} />
                      </button>
                    </div>
                  )}
                  {(addresses.length === 0 || showNewAddrForm) && (
                    <form onSubmit={handleSaveNewAddr} className="cd-addr-form">
                      {showNewAddrForm && addresses.length > 0 && (
                        <button type="button" className="cd-text-btn" style={{ marginBottom: 12 }}
                          onClick={() => { setShowNewAddrForm(false); setSelectedAddr(addresses.find(a => a.is_default) || addresses[0]); }}>
                          <FiArrowLeft size={13} /> Use saved address
                        </button>
                      )}
                      <div className="cd-name-row">
                        <div className="cd-field">
                          <label className="cd-label">Full Name *</label>
                          <input className="cd-input" value={newAddr.name} onChange={e => setNewAddr(a => ({ ...a, name: e.target.value }))} placeholder="Full name" required />
                        </div>
                        <div className="cd-field">
                          <label className="cd-label">Phone *</label>
                          <input className="cd-input" value={newAddr.phone} onChange={e => setNewAddr(a => ({ ...a, phone: e.target.value }))} placeholder="10-digit mobile" required />
                        </div>
                      </div>
                      <div className="cd-field">
                        <label className="cd-label">Address Line 1 *</label>
                        <input className="cd-input" value={newAddr.address_line1} onChange={e => setNewAddr(a => ({ ...a, address_line1: e.target.value }))} placeholder="House no., Street" required />
                      </div>
                      <div className="cd-field">
                        <label className="cd-label">Address Line 2 <span className="cd-optional">(optional)</span></label>
                        <input className="cd-input" value={newAddr.address_line2} onChange={e => setNewAddr(a => ({ ...a, address_line2: e.target.value }))} placeholder="Landmark, Area" />
                      </div>
                      <div className="cd-three-col">
                        <div className="cd-field">
                          <label className="cd-label">Pincode *</label>
                          <input className="cd-input" value={newAddr.pincode} onChange={e => setNewAddr(a => ({ ...a, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) }))} placeholder="6-digit" required />
                        </div>
                        <div className="cd-field">
                          <label className="cd-label">City *</label>
                          <input className="cd-input" value={newAddr.city} onChange={e => setNewAddr(a => ({ ...a, city: e.target.value }))} placeholder="City" required />
                        </div>
                        <div className="cd-field">
                          <label className="cd-label">State *</label>
                          <select className="cd-input cd-select" value={newAddr.state} onChange={e => setNewAddr(a => ({ ...a, state: e.target.value }))} required>
                            <option value="">State</option>
                            {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                      </div>
                      <button type="submit" className="cd-btn cd-btn--primary" disabled={loading}>
                        {loading ? <span className="cd-spinner-wrap"><span className="cd-spinner" /> Saving...</span> : <><FiMapPin size={15} /> Save & Continue</>}
                      </button>
                    </form>
                  )}
                </>
              )}
            </div>
          )}

          {/* Payment step */}
          {step === 'payment' && (
            <div className="cd-section">
              {selectedAddr && (
                <div className="cd-addr-summary">
                  <div className="cd-addr-summary__left">
                    <FiMapPin size={14} />
                    <div>
                      <p className="cd-addr-summary__name">{selectedAddr.name}</p>
                      <p className="cd-addr-summary__line">{selectedAddr.address_line1}, {selectedAddr.city}, {selectedAddr.state} – {selectedAddr.pincode}</p>
                    </div>
                  </div>
                  <button className="cd-text-btn" onClick={() => setStep('address')}>Change</button>
                </div>
              )}

              <p className="cd-section__title" style={{ marginTop: 16 }}>Payment Method</p>

              <p className="cd-pm-group-label">Pay Online</p>
              <div className="cd-payment-list">
                {PAYMENT_OPTIONS.filter(pm => !pm.isCod).map(pm => (
                  <div key={pm.value} className={`cd-payment-opt ${paymentMethod === pm.value ? 'cd-payment-opt--selected' : ''}`} onClick={() => setPaymentMethod(pm.value)}>
                    <span className="cd-payment-opt__icon">{pm.icon}</span>
                    <div className="cd-payment-opt__info">
                      <p className="cd-payment-opt__label">
                        {pm.label}
                        {pm.badge && <span className="cd-pm-badge cd-pm-badge--offer">{pm.badge}</span>}
                      </p>
                      <p className="cd-payment-opt__desc">{pm.desc}</p>
                      {paymentMethod === pm.value && couponData && (
                        <p className="cd-upi-offer">
                          <FiZap size={11} /> 10% prepaid discount auto-applied! Saving ₹{prepaidDiscount.toFixed(0)}
                        </p>
                      )}
                    </div>
                    <div className={`cd-radio ${paymentMethod === pm.value ? 'cd-radio--checked' : ''}`}>
                      {paymentMethod === pm.value && <div className="cd-radio__dot" />}
                    </div>
                  </div>
                ))}
              </div>

              <p className="cd-pm-group-label" style={{ marginTop: 12 }}>Other</p>
              <div className="cd-payment-list">
                {PAYMENT_OPTIONS.filter(pm => pm.isCod).map(pm => (
                  <div key={pm.value} className={`cd-payment-opt ${paymentMethod === pm.value ? 'cd-payment-opt--selected' : ''}`} onClick={() => setPaymentMethod(pm.value)}>
                    <span className="cd-payment-opt__icon">{pm.icon}</span>
                    <div className="cd-payment-opt__info">
                      <p className="cd-payment-opt__label">{pm.label}</p>
                      <p className="cd-payment-opt__desc">{pm.desc}</p>
                    </div>
                    <div className={`cd-radio ${paymentMethod === pm.value ? 'cd-radio--checked' : ''}`}>
                      {paymentMethod === pm.value && <div className="cd-radio__dot" />}
                    </div>
                  </div>
                ))}
              </div>
              <p className="cd-cod-note">COD may not be available for all pincodes</p>

              {/* ── Discount section ── */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14 }}>

                {/* Auto prepaid banner */}
                {couponData && couponCode === PREPAID_COUPON && (
                  <div className="cd-coupon-applied-banner">
                    <FiCheck size={13} /> UPI10 applied — saving ₹{prepaidDiscount.toFixed(0)}!
                  </div>
                )}

                {/* Manual promo code */}
                {promoData ? (
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: '#eff6ff', border: '1px solid #bfdbfe',
                    borderRadius: 8, padding: '10px 14px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <FiTag size={13} style={{ color: '#2563eb' }} />
                      <p style={{ fontSize: '0.83rem', color: '#1d4ed8', margin: 0 }}>
                        <strong>{promoCode}</strong> — saving ₹{promoDiscount.toFixed(0)}
                      </p>
                    </div>
                    <button onClick={() => { setPromoData(null); setPromoCode(''); }}
                      style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '1rem' }}>✕</button>
                  </div>
                ) : (
                  <div className="cd-coupon">
                    <input className="cd-input cd-coupon__input"
                      placeholder="Extra promo code (optional)"
                      value={promoCode}
                      onChange={e => setPromoCode(e.target.value.toUpperCase())} />
                    <button className="cd-coupon__btn" onClick={handleApplyPromo} disabled={promoLoading || !promoCode.trim()}>
                      <FiTag size={14} /> {promoLoading ? '...' : 'Apply'}
                    </button>
                  </div>
                )}

              </div>

              <div className="cd-note-row">
                <label className="cd-label">Order Note <span className="cd-optional">(optional)</span></label>
                <input className="cd-input" value={note} onChange={e => setNote(e.target.value)} placeholder="Special instructions..." />
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="cd-footer">
          <div className="cd-summary">
            <button className="cd-summary__toggle" onClick={() => setSummaryOpen(true)}>
              <div className="cd-summary__toggle-left">
                <FiShoppingBag size={14} />
                <span className="cd-summary__label">
                  {displayItems.length} Item{displayItems.length !== 1 ? 's' : ''} · {displayItems.reduce((sum, item) => sum + item.quantity, 0)} Qty
                </span>
              </div>
              <div className="cd-summary__toggle-right">
                <div style={{ textAlign: 'right' }}>
                  {totalDiscount > 0 && step === 'payment' && (
                    <p style={{ fontSize: 10, color: '#2d6a4f', fontWeight: 600, margin: 0 }}>
                      You save ₹{totalDiscount.toFixed(0)}
                    </p>
                  )}
                  <span className="cd-summary__total-preview">
                    ₹{step === 'payment' ? total.toFixed(0) : (subtotal + shipping).toFixed(0)}
                  </span>
                </div>
                <FiChevronUp size={14} />
              </div>
            </button>

            <div className="cd-summary__compact">
              {subtotal > 0 && (
                <div className="cd-summary__row">
                  <span>Subtotal</span><span>₹{subtotal.toFixed(0)}</span>
                </div>
              )}
              {subtotal > 0 && (
                <div className="cd-summary__row">
                  <span>Shipping</span>
                  <span style={{ color: shipping === 0 ? '#2d6a4f' : 'inherit', fontWeight: shipping === 0 ? 700 : 400 }}>
                    {shipping === 0 ? 'FREE' : `₹${shipping}`}
                  </span>
                </div>
              )}
              {prepaidDiscount > 0 && step === 'payment' && (
                <div className="cd-summary__row cd-summary__row--discount">
                  <span>Prepaid Discount (UPI10)</span>
                  <span>–₹{prepaidDiscount.toFixed(0)}</span>
                </div>
              )}
              {promoDiscount > 0 && step === 'payment' && (
                <div className="cd-summary__row cd-summary__row--discount">
                  <span>Promo ({promoCode})</span>
                  <span>–₹{promoDiscount.toFixed(0)}</span>
                </div>
              )}
              <div className="cd-summary__row cd-summary__row--total">
                <span>Total</span>
                <span>₹{step === 'payment' ? total.toFixed(0) : (subtotal + shipping).toFixed(0)}</span>
              </div>
            </div>
          </div>

          {step === 'payment' && (
            <button className="cd-btn cd-btn--place" onClick={handlePlaceOrder} disabled={placing}>
              {placing
                ? <span className="cd-spinner-wrap"><span className="cd-spinner" /> Placing Order...</span>
                : `Place Order · ₹${total.toFixed(0)}`
              }
            </button>
          )}
          <p className="cd-footer__secure">🔒 100% Secure Checkout</p>
        </div>
      </div>
    </>
  );
};

export default CheckoutDrawer;